import React, { useState } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Box } from "@material-ui/core";
import { CardWrapper } from "../../util/theme";
import {
  CheckEntryCheckCircleSharpIcon,
  CheckEntryContentWrapper,
  CheckEntryErrorIcon,
  CheckEntryHeader,
  CheckEntryText,
  FormsHeader,
  FormTab,
  PageGrid,
} from "./style";
import FixedActivityTab from "./FixedActivityTab";
import { ProcurementMethodAccessControl } from "../../shared";
import { Procurement_Method } from "../../util/constants";
import { useParams } from "react-router-dom";
import { useProcurementMethod } from "../../hooks/queries/useProcurement";

/**
 * This is the activity tabs component created to allow reusability and prevent code duplication
 * It receives the follows props
 *
 * @param tabs - The array of tabs
 * @param tabs[0].title - The name used to identify the tab
 * @param tabs[0].allowClick - This determines if clicking on the tab makes it switch to that tab
 * @param tabs[0].component - The component to be rendered
 *
 * @param entries - Array of entries rendered in the check entry progress sidebar
 *
 * @param defaultActiveKey - You can use this to set the default active key (eg you want to go to the review tab immediately)
 *
 * @param loading - This determines whether the component should show a loader or not
 */

const ActivityTabs = ({
  tabs,
  entries,
  defaultActiveKey = 0,
  loading = false,
  completeEntries,
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveKey);
  
  const { procurementId } = useParams()
  const { method , isSuccess} = useProcurementMethod({ procurementId })

  const onPrev = () => {
    setActiveTab(Math.max(0, activeTab - 1));
  };

  const onNext = () => {
    setActiveTab(Math.min(activeTab + 1, tabs.length - 1));
  };

  const onClickTab = (index) => {
    switch (index) {
      // Allow go to first tab
      case 0: {
        setActiveTab(0);
        break;
      }

      // Allow go to last tab because it's always the review tab
      case tabs.length - 1: {
        setActiveTab(tabs.length - 1);
        break;
      }

      // Conditionally allow go to other tabs based on the user's props
      default: {
        if (tabs[index]?.allowClick) {
          setActiveTab(index);
        }
      }
    }
  };

  return (
    <PageGrid>
      <CardWrapper width={"100%"} padding={0}>
        <FormsHeader>
          {tabs.map(({ title }, index) => (
            <FormTab isActive={tabs[activeTab]?.title === title} style={{ cursor: "pointer" }} onClick={() => onClickTab(index)}>
              {title}
            </FormTab>
          ))}
        </FormsHeader>
        {loading ? (
          <Box display="flex" pt={10} pb={10} flexDirection="column" alignItems="center">
            <CircularProgress />
            <Box mt={5}>
              <p>Loading details</p>
            </Box>
          </Box>
        ) : (
          React.cloneElement(tabs[activeTab]?.component, {
            onPrev,
            onNext,
          })
        )}
      </CardWrapper>
      <FixedActivityTab>
        <CardWrapper>
          <CheckEntryHeader>Check Entries</CheckEntryHeader>
          {entries.map((entry, index) => (
            <CheckEntryContentWrapper>
              <ProcurementMethodAccessControl
                noLoader
                isSuccess={isSuccess}
                method={method}
                allowedMethod={Object.values(Procurement_Method).filter((value) => value !== Procurement_Method.REQ_QUOTATION)}
              >
                {activeTab > index ? <CheckEntryCheckCircleSharpIcon /> : <CheckEntryErrorIcon />}
              </ProcurementMethodAccessControl>
              <ProcurementMethodAccessControl
                noLoader
                isSuccess={isSuccess}
                method={method}
                allowedMethod={[Procurement_Method.REQ_QUOTATION]}
              >
                {completeEntries?.includes(entry) ? <CheckEntryCheckCircleSharpIcon /> : <CheckEntryErrorIcon />}
              </ProcurementMethodAccessControl>
              <CheckEntryText>{entry}</CheckEntryText>
            </CheckEntryContentWrapper>
          ))}
        </CardWrapper>
      </FixedActivityTab>
    </PageGrid>
  );
};

ActivityTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      allowClick: PropTypes.bool,
      component: PropTypes.node,
    })
  ),
  entries: PropTypes.arrayOf(PropTypes.string),
  defaultActiveKey: PropTypes.number,
};

export default ActivityTabs;
